import {useState, type ReactNode} from "react";
import {Button} from "@/primitives/Button";
import {Modal} from "@/primitives/Modal";
import {QR_CODE_LARGE_SIDELENGTH, QR_CODE_SIDELENGTH} from "@/shared/constants";

interface QrCodeCardProps {
	qrCodeImageUrl: string;
	/** Names the code — heading of the enlarged view, and the image's alternative text. */
	title: string;
	name: string;
	login: string;
	description: string;
	actions: ReactNode;
	/** Extra footer buttons for the enlarged view; it always closes itself. */
	enlargedActions?: ReactNode;
}

/**
 * QR code card with the blue photo panel, name heading, login line, and description text.
 * Styled by the ".blog-card" rules in src/main/less/qrCode.css (bundled into base.css).
 * Clicking the code opens it large enough to be scanned off the screen.
 */
export function QrCodeCard({
	qrCodeImageUrl,
	title,
	name,
	login,
	description,
	actions,
	enlargedActions,
}: QrCodeCardProps) {
	const [enlarged, setEnlarged] = useState(false);

	return (
		<div className="row">
			<div className="blog-card">
				<div className="meta">
					<div className="photo">
						<div className="qrCodeImageContainer">
							<button type="button" onClick={() => setEnlarged(true)} title={title}>
								<img
									src={qrCodeImageUrl}
									width={QR_CODE_SIDELENGTH}
									height={QR_CODE_SIDELENGTH}
									alt={title}
								/>
							</button>
						</div>
					</div>
				</div>
				<div className="description">
					<h1>{name}</h1>
					<div className="qr-login">{login}</div>
					<p>{description}</p>
					<div className="qr-actions">{actions}</div>
				</div>
			</div>
			<Modal.Root open={enlarged} onOpenChange={setEnlarged}>
				<Modal.Content size="md">
					<Modal.Header>
						<Modal.Title>{title}</Modal.Title>
					</Modal.Header>
					<Modal.Body className="text-center">
						<div className="qrCodeEnlarged">
							<img
								src={qrCodeImageUrl}
								width={QR_CODE_LARGE_SIDELENGTH}
								height={QR_CODE_LARGE_SIDELENGTH}
								alt={title}
							/>
						</div>
					</Modal.Body>
					<Modal.Footer>
						{enlargedActions}
						<Modal.Close render={<Button variant="secondary" type="button"/>}>
							{messages["dialog.close"]}
						</Modal.Close>
					</Modal.Footer>
				</Modal.Content>
			</Modal.Root>
		</div>
	);
}
